import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { Spinner } from '@/components/ui/Spinner';
import { Ionicons } from '@expo/vector-icons';
import { spacing, type as typo, useAppTheme } from '@/theme';
import { Button } from './Button';
import { ScaledSheet } from '@/theme/responsive';
import { useTranslation } from '@/i18n/useTranslation';

type StateKind = 'loading' | 'empty' | 'error' | 'offline';

interface Props {
  type: StateKind;
  title: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
  icon?: keyof typeof Ionicons.glyphMap;
}

const iconMap: Record<StateKind, keyof typeof Ionicons.glyphMap> = {
  loading: 'hourglass-outline',
  empty: 'file-tray-outline',
  error: 'alert-circle-outline',
  offline: 'cloud-offline-outline',
};

/** One shared component for every loading/empty/error/offline moment in the app — so
 *  these states always look and feel the same regardless of which screen triggers them. */
export function ScreenState({ type: kind, title, message, actionLabel, onAction, icon }: Props) {
  const { colors } = useAppTheme();
  const { tText } = useTranslation();
  // The server can take a while to wake up after a quiet spell: say so rather than leave a spinner that looks stuck.
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (kind !== 'loading') return undefined;
    const timer = setTimeout(() => setSlow(true), 6000);
    return () => {
      clearTimeout(timer);
      setSlow(false);
    };
  }, [kind]);

  return (
    <View style={styles.container} accessibilityLiveRegion="polite">
      {kind === 'loading' ? (
        <Spinner size="large" color={colors.accent} />
      ) : (
        <View style={[styles.iconWrap, { backgroundColor: kind === 'error' ? colors.errorSoft : colors.surfaceMuted }]}>
          <Ionicons name={icon ?? iconMap[kind]} size={30} color={kind === 'error' ? colors.error : colors.textTertiary} />
        </View>
      )}
      <Text style={[typo.h3, styles.title, { color: colors.textPrimary }]}>{title}</Text>
      {message ? <Text style={[typo.body, styles.message, { color: colors.textSecondary }]}>{message}</Text> : null}
      {kind === 'loading' && slow ? (
        <Text style={[typo.body, styles.message, { color: colors.textTertiary }]}>
          {tText('This is taking longer than usual. Please wait a moment.', 'স্বাভাবিকের চেয়ে একটু বেশি সময় লাগছে। অনুগ্রহ করে একটু অপেক্ষা করুন।')}
        </Text>
      ) : null}
      {actionLabel && onAction ? (
        <Button label={actionLabel} onPress={onAction} variant="outlined" fullWidth={false} size="sm" style={styles.action} />
      ) : null}
    </View>
  );
}

const styles = ScaledSheet.create({
  container: { minHeight: 280, alignItems: 'center', justifyContent: 'center', padding: spacing.xxl, gap: spacing.xs },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
  },
  title: { textAlign: 'center' },
  message: { textAlign: 'center' },
  action: { marginTop: spacing.md },
});
